import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { CircularProgress, FormControl, MenuItem, Select } from "@mui/material";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { HttpService } from "../../../service/HttpService";
import FullScreenLoader from "../../../components/Loader/FullScreenLoader";
import { useAuth } from "../../../context/AuthProvider";
import moment from "moment";
// Reuse styles from original page
import "../RockwoolRootingHistory/RockwoolRootingHistory.scss";

const FILTER_STORAGE_KEY = "rockwoolRootingHistoryDateFilter";

const TrayNotRootedTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null;
  const {
    trayLabel,
    total_clones,
    rooted_clones,
    rooting_success_percentage,
    not_rooted,
  } = payload[0].payload || {};

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: 4,
        padding: 12,
      }}
    >
      <div>
        <strong>Tray ID : </strong> {trayLabel ?? "-"}
      </div>
      <div>
        <strong>Total Clones : </strong> {total_clones ?? "-"}
      </div>
      <div>
        <strong>Rooted Clones : </strong> {rooted_clones ?? "-"}
      </div>
      <div>
        <strong>Not-Rooted Clones : </strong> {not_rooted ?? "-"}
      </div>
      <div>
        <strong>Rooting Success : </strong>{" "}
        {rooting_success_percentage
          ? `${parseFloat(rooting_success_percentage)}%`
          : "-"}
      </div>
    </div>
  );
};

function RockwoolRootingAverage() {
  const { setShowLoginPopup, showLoginPopup } = useAuth();
  const httpService = useMemo(
    () => new HttpService(setShowLoginPopup),
    [setShowLoginPopup]
  );

  const storedFilters = JSON.parse(
    localStorage.getItem(FILTER_STORAGE_KEY) || "null"
  );

  const hasValidStoredStart =
    storedFilters?.startDate && dayjs(storedFilters.startDate).isValid();
  const hasValidStoredEnd =
    storedFilters?.endDate && dayjs(storedFilters.endDate).isValid();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  // New: cultivar filter
  const [cultivars, setCultivars] = useState([]);
  const [cultivarFilter, setCultivarFilter] = useState("");

  // Keep type as a fixed default (cluster) to preserve API grouping; not exposed to UI
  const typeFixed = "cluster";

  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(100);
  const [historyRowCount, setHistoryRowCount] = useState(0);
  const [historySearch, setHistorySearch] = useState("");
  const [selectedPlantId, setSelectedPlantId] = useState(null);
  const [selectedDaysFromCut, setSelectedDaysFromCut] = useState(null);
  const [pendingStartDate, setPendingStartDate] = useState(startDate || "");
  const [pendingEndDate, setPendingEndDate] = useState(endDate || "");

  const isMountedRef = useRef(false);
  const historySectionRef = useRef(null);

  const scrollToHistorySection = useCallback(() => {
    if (historySectionRef.current) {
      historySectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Validate date range
  useEffect(() => {
    if (
      startDate &&
      endDate &&
      dayjs(startDate).isValid() &&
      dayjs(endDate).isValid() &&
      dayjs(endDate).isBefore(dayjs(startDate), "day")
    ) {
      const clearedEnd = "";
      setEndDate(clearedEnd);
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ startDate, endDate: clearedEnd })
      );
    } else {
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ startDate, endDate })
      );
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
  }, []);

  // Fetch cultivar list for dropdown
  useEffect(() => {
    const fetchCultivars = async () => {
      try {
        const res = await httpService.get("plant/get_plant");
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];
        const sorted = (list || [])
          .slice()
          .sort((a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || ""))
          );
        setCultivars(sorted);

        // Set default selected cultivar to alphabetically first, if none selected yet
        if (!cultivarFilter && sorted.length > 0) {
          setCultivarFilter(sorted[0].id);
        }
      } catch (e) {
        setCultivars([]);
      }
    };
    fetchCultivars();
  }, [httpService, cultivarFilter]);

  const fetchRootingHistory = async () => {
    setLoading(true);
    try {
      const trimmedSearch = search.trim();
      let url =
        `rockwool/rooting-success-by-tray` +
        `?page=${page + 1}&pageSize=${pageSize}` +
        `${trimmedSearch ? `&search=${trimmedSearch}` : ""}` +
        `${cultivarFilter ? `&plant_id=${cultivarFilter}` : ""}`;

      const hasValidStart = !!startDate && dayjs(startDate).isValid();
      const hasValidEnd = !!endDate && dayjs(endDate).isValid();

      if (hasValidStart && hasValidEnd) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }

      const res = await httpService.get(url);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const total =
        res?.data?.pagination?.total ||
        res?.data?.meta?.total ||
        list.length ||
        0;

      if (!isMountedRef.current) return;
      setRows(list);
      setRowCount(total);
    } catch (_) {
      if (!isMountedRef.current) return;
      setRows([]);
      setRowCount(0);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRootingHistory();
  }, [
    page,
    pageSize,
    startDate,
    endDate,
    search,
    cultivarFilter,
    httpService,
    showLoginPopup,
  ]);

  const fetchHistoryDetails = async () => {
    setHistoryLoading(true);
    try {
      const trimmedSearch = historySearch.trim();
      let url =
        `rockwool/rooting-success` +
        `?page=${historyPage + 1}&pageSize=${historyPageSize}` +
        `${trimmedSearch ? `&search=${trimmedSearch}` : ""}` +
        `${cultivarFilter ? `&plant_id=${cultivarFilter}` : ""}`;

      // If a tray is selected from table/chart, prefer that as a filter
      if (!trimmedSearch && selectedPlantId) {
        url += `&tray_id=${selectedPlantId}`;
      }

      const hasValidStart =
        !trimmedSearch && !!startDate && dayjs(startDate).isValid();
      const hasValidEnd =
        !trimmedSearch && !!endDate && dayjs(endDate).isValid();

      if (hasValidStart && hasValidEnd) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }

      const res = await httpService.get(url);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const total =
        res?.data?.pagination?.total ||
        res?.data?.meta?.total ||
        list.length ||
        0;

      if (!isMountedRef.current) return;
      setHistoryRows(list);
      setHistoryRowCount(total);
    } catch (_) {
      if (!isMountedRef.current) return;
      setHistoryRows([]);
      setHistoryRowCount(0);
    } finally {
      if (!isMountedRef.current) return;
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoryDetails();
  }, [
    historyPage,
    historyPageSize,
    historySearch,
    selectedPlantId,
    selectedDaysFromCut,
    startDate,
    endDate,
    cultivarFilter,
    httpService,
    showLoginPopup,
  ]);

  const chartData = useMemo(() => {
    if (!rows?.length) return [];

    return rows
      .filter(
        (r) =>
          r.total_clones != null &&
          r.total_clones !== "-" &&
          r.rooting_success_percentage != null &&
          r.rooting_success_percentage !== "-"
      )
      .map((r, index) => {
        const trayId = r.tray_id || r.cluster || `Tray-${index + 1}`;
        const totalClones = Number(r.total_clones ?? 0);
        const rootingSuccess = Number(r.rooting_success_percentage ?? 0);
        const notRootedClones = Number(r.not_rooted ?? 0);
        const rootedClones = totalClones - notRootedClones;

        return {
          trayLabel: String(trayId),
          total_clones: totalClones,
          rooted_clones: rootedClones,
          rooting_success_percentage: rootingSuccess,
          not_rooted: notRootedClones,
          axisLabel: `${trayId}`,
        };
      });
  }, [rows]);

  const summaryColumns = [
    {
      field: "tray_id",
      headerName: "Tray ID",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params.row?.tray_id || "-"}</div>
      ),
    },
    {
      field: "total_clones",
      headerName: "Total Clones",
      flex: 1,
      minWidth: 95,
      headerClassName: "headerclass",
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params.row?.total_clones || "-"}</div>
      ),
    },
    {
      field: "rooted_clones",
      headerName: "Rooted Clones",
      flex: 1,
      minWidth: 95,
      headerClassName: "headerclass",
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.rooted_clones || "-"}
        </div>
      ),
    },
    {
      field: "not_rooted",
      headerName: "Not Rooted Clones",
      flex: 1,
      minWidth: 100,
      headerClassName: "headerclass",
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params.row?.not_rooted ?? "-"}</div>
      ),
    },
    {
      field: "rooting_success_percentage",
      headerName: "Average Rooting Success",
      flex: 1,
      minWidth: 120,
      headerClassName: "headerclass",
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.rooting_success_percentage
            ? `${parseFloat(params.row?.rooting_success_percentage)}%`
            : "-"}
        </div>
      ),
    },
  ];

  const clusterColumns = [
    {
      field: "cultivar",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params?.row?.cultivar || "-"}</div>
      ),
    },
    {
      field: "tray_name",
      headerName: "Tray ID",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params?.row?.tray_name || "-"}</div>
      ),
    },
    {
      field: "clone_name",
      headerName: "Clone ID",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>{params?.row?.clone_name || "-"}</div>
      ),
    },
    {
      field: "date",
      headerName: "Rockwool Date",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => {
        return (
          <div style={{ padding: "10px" }}>
            {moment(params?.row?.date).format("MM-DD-YYYY")}
          </div>
        );
      },
    },
    {
      field: "rooting_status",
      headerName: "Rooting Status",
      flex: 1,
      minWidth: 190,
    },
  ];

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="maintenance-view">
        <div className="page-title">
          <div className="title-wrap">
            <h1>Average Rooting Success per Tray</h1>
          </div>
        </div>

        <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
          <div className="header_search-wrap">
            <div className="header_search_bar common_content d-flex">
              <div
                className="input-group input_group"
                style={{ display: "flex", gap: "12px", alignItems: "center" }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={pendingStartDate ? dayjs(pendingStartDate) : null}
                    disableFuture
                    onChange={(newStartDate) => {
                      setPendingStartDate(
                        newStartDate
                          ? dayjs(newStartDate).format("YYYY-MM-DD")
                          : ""
                      );
                    }}
                    format="YYYY-MM-DD"
                    sx={{ minWidth: "200px" }}
                  />
                  <DatePicker
                    label="End Date"
                    value={pendingEndDate ? dayjs(pendingEndDate) : null}
                    disableFuture
                    minDate={
                      pendingStartDate && dayjs(pendingStartDate).isValid()
                        ? dayjs(pendingStartDate)
                        : undefined
                    }
                    onChange={(newEndDate) => {
                      setPendingEndDate(
                        newEndDate ? dayjs(newEndDate).format("YYYY-MM-DD") : ""
                      );
                    }}
                    format="YYYY-MM-DD"
                    sx={{ minWidth: "200px" }}
                  />
                </LocalizationProvider>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                style={{ minWidth: 120, marginLeft: 8, fontSize: "16px" }}
                onClick={() => {
                  setStartDate(pendingStartDate);
                  setEndDate(pendingEndDate);
                  setPage(0);
                }}
              >
                Get Details
              </button>
            </div>
          </div>
        </div>

        <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
          <div className="input-group input_group">
            {/* Replaced Clone/Cluster filter with Cultivar filter */}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value={cultivarFilter}
                onChange={(e) => {
                  setCultivarFilter(e.target.value);
                  setPage(0);
                  // Reset details context when selecting cultivar from dropdown
                  setSelectedPlantId(null);
                  setSelectedDaysFromCut(null);
                  setHistoryPage(0);
                }}
                displayEmpty
                style={{ backgroundColor: "white" }}
                renderValue={(selected) => {
                  if (!selected) return <span>Select Cultivar</span>;
                  const found = cultivars.find(
                    (c) => String(c.id) === String(selected)
                  );
                  return found ? found.name : "Select Cultivar";
                }}
              >
                <MenuItem value="">Select Cultivar</MenuItem>
                {cultivars.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="header_search-wrap">
            {/* <div className="header_search_bar common_content">
              <div
                className="input-group input_group"
                style={{ display: "flex", gap: "12px", alignItems: "center" }}
              >
                <button className="btn search-btn search_btn">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 21 21"
                    fill="none"
                  >
                    <circle
                      cx="9.98856"
                      cy="9.98856"
                      r="8.98856"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.2402 16.7071L19.7643 20.222"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <input
                  type="search"
                  className="form-control form_control"
                  placeholder="Search..."
                  aria-label="Search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                />
              </div>
            </div> */}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            marginTop: "20px",
            overflowX: "auto",
          }}
        >
          <div>
            <div
              className="responsive-table"
              // style={{
              //   height: 480,
              //   width:
              //     window.innerWidth >= 1500
              //       ? "630px" // Large screens
              //       : window.innerWidth >= 800
              //       ? "800px" // Medium screens
              //       : "100%", // Mobile
              // }}
            >
              <DataGrid
                rows={rows}
                getRowId={(r) =>
                  r.id ||
                  r._id ||
                  `${r.cultivar || "cultivar"}-${r.clone_id || "clone"}-${
                    r.first_partially_rooted || Math.random()
                  }`
                }
                columns={summaryColumns}
                loading={loading}
                rowCount={rowCount}
                pageSizeOptions={[15, 50, 100]}
                paginationModel={{ pageSize, page }}
                paginationMode="server"
                onPaginationModelChange={({ page, pageSize }) => {
                  setPage(page);
                  setPageSize(pageSize);
                }}
                getRowHeight={() => "auto"}
                sx={{
                  border: "1px solid #E0E0E0",
                  [`& .${gridClasses.cell}`]: { py: 0.5 },
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                }}
                showCellVerticalBorder
                showColumnVerticalBorder
                onRowClick={(params) => {
                  const trayIdFromRow = params?.row?.tray_id;
                  if (trayIdFromRow) {
                    setSelectedPlantId(String(trayIdFromRow));
                    setSelectedDaysFromCut(null);
                    setHistoryPage(0);
                    if (historySearch) {
                      setHistorySearch("");
                    }
                    scrollToHistorySection();
                  }
                }}
              />
            </div>
          </div>

          <div>
            <div
              style={{
                backgroundColor: "#fff",
                border: "1px solid #E0E0E0",
                borderRadius: "4px",
                padding: "20px",
                height: 400,
                minWidth: "950px",
                minHeight: "400px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <h3
                style={{
                  marginBottom: "16px",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                Avg Rooting Success vs Tray ID
              </h3>

              {loading ? (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={48} />
                </div>
              ) : chartData.length > 0 ? (
                <LineChart
                  width={800}
                  height={340}
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 80,
                  }}
                  style={{ cursor: "pointer" }}
                  onClick={(chartEvent) => {
                    const payload =
                      chartEvent?.activePayload && chartEvent.activePayload[0];
                    const trayFromPoint = payload?.payload?.trayLabel;
                    if (trayFromPoint) {
                      setSelectedPlantId(String(trayFromPoint));
                      setSelectedDaysFromCut(null);
                      setHistoryPage(0);
                      if (historySearch) {
                        setHistorySearch("");
                      }
                      scrollToHistorySection();
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="axisLabel"
                    textAnchor="end"
                    height={70}
                    interval={0}
                    angle={-45}
                    style={{ fontSize: "12px" }}
                  />
                  <YAxis
                  // label={{
                  //   value: "Rooting Success (%)",
                  //   angle: -90,
                  //   position: "left",
                  //   offset: 0,
                  //   style: {
                  //     marginRight: "10px",
                  //   },
                  // }}
                  />

                  <Tooltip
                    content={<TrayNotRootedTooltip />}
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                  {/* <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ bottom: 10 }}
                  /> */}
                  <Line
                    type="monotone"
                    dataKey="rooting_success_percentage"
                    name="Rooting Success"
                    stroke="#0066CC"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  {/* <Line
                    type="monotone"
                    dataKey="not_rooted"
                    name="Not-Rooted Clones"
                    stroke="#00A272"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  /> */}
                </LineChart>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "600px",
                    height: "400px",
                    color: "#999",
                  }}
                >
                  No data available for chart
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: "32px" }} ref={historySectionRef}>
          <div className="page-title pb-1 pt-2">
            <div className="title-wrap">
              <h1>Last Entry of Rooting Status</h1>
            </div>
          </div>
          <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-1">
            {/* <div className="header_search-wrap">
              <div className="header_search_bar common_content">
                <div className="input-group input_group">
                  <button className="btn search-btn search_btn">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 21 21"
                      fill="none"
                    >
                      <circle
                        cx="9.98856"
                        cy="9.98856"
                        r="8.98856"
                        stroke="#495057"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16.2402 16.7071L19.7643 20.222"
                        stroke="#495057"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <input
                    type="search"
                    className="form-control form_control"
                    placeholder="Search..."
                    aria-label="Search"
                    value={historySearch}
                    onChange={(e) => {
                      setHistorySearch(e.target.value);
                      setHistoryPage(0);
                    }}
                  />
                </div>
              </div>
            </div> */}
          </div>
          <div>
            <div
              style={{
                height: `${historyRows?.length > 0 ? "" : "200px"}`,
                width: "100%",
                marginTop: 12,
              }}
            >
              <DataGrid
                rows={historyRows}
                getRowId={(r) =>
                  r.id ||
                  r._id ||
                  `${r.cultivar || "cultivar"}-${r.clone_id || "clone"}-${
                    r.first_partially_rooted || Math.random()
                  }`
                }
                columns={clusterColumns}
                loading={historyLoading}
                rowCount={historyRowCount}
                pageSizeOptions={[15, 50, 100]}
                paginationModel={{
                  pageSize: historyPageSize,
                  page: historyPage,
                }}
                paginationMode="server"
                onPaginationModelChange={({ page, pageSize }) => {
                  setHistoryPage(page);
                  setHistoryPageSize(pageSize);
                }}
                getRowHeight={() => "auto"}
                getRowClassName={(params) => {
                  return params.row?.highlight_red ? "highlight-red-row" : "";
                }}
                sx={{
                  border: "1px solid #E0E0E0",
                  [`& .${gridClasses.cell}`]: { py: 0.5 },
                }}
                autoHeight
                showCellVerticalBorder
                showColumnVerticalBorder
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RockwoolRootingAverage;
